import { defineStore } from 'pinia'
import {
  BlackInfo,
  ISessionChatRecordRequest,
  ISessionChatRecordResponseParam,
  MsgType,
  UserType,
  queryCsHistoryRecord,
  listInternalNotes
} from '@/server/session_request'
import { v4 as uuidv4 } from 'uuid'
// import { UserType } from './useWebSocketStore'
import { timeUtilNowMoment } from '@/utils/timeUtil'
import { useUserStore } from './userStore'
import { useChatListStore } from '@/stores/chatListStore'
import { MsgTabEnum } from '@/server/message_request'
import { queryHistoryRecord } from '@/server/check_request'

/**
 * message-store 只记录和处理当前聊天内容。
 * 1. 请求接口，拿到最近20条消息，进行处理
 * 2. 根据socket内容 。 处理塞入
 */
export const useMessageStore = defineStore('message', {
  state: () => {
    return {
      sessionId: '',
      userId: '',
      senderImg: '',
      senderName: '',
      geterImg: '',
      data: [] as any,
      newMessageNumber: 0,
      translationSwitch: false,
      aiSwitch: false,
      blackInfo: {
        block: false
      } as BlackInfo,
      fastReply: '',
      isNeedUpdate: true,
      status: '',
      myself: false,
      readSessions: new Map(),
      msgTab: MsgTabEnum.All,
      topPlace: false,
      mobileInChat: false,
      autoEndCountdownStartTime: '',
      // 「同事对话」只读监看模式：自己不是会话成员，历史走监管接口（不校验成员），不接收 WebSocket 推送，靠轮询补新消息
      colleagueMode: false,
      // 内部回复：输入框当前是「回复」(reply) 还是「内部回复」(internal)；内部消息只有同事看得到，不在玩家的聊天记录里
      inputMode: 'reply' as 'reply' | 'internal',
      internalNotes: [] as any[],
      internalNotesSession: ''
    }
  },
  actions: {
    setInputMode(mode: 'reply' | 'internal') {
      this.inputMode = mode
    },
    isInternalMode() {
      return this.inputMode === 'internal'
    },

    /**
     * 拉这条会话的内部消息并混进聊天窗口。后端不是成员时返回空列表（不报错）。
     */
    async loadInternalNotes() {
      const sid = this.sessionId
      if (!sid) return
      try {
        const res: any = await listInternalNotes(sid)
        // 请求回来时已经切走了就丢掉
        if (this.sessionId !== sid) return
        if (res && res.code == '0' && Array.isArray(res.data)) {
          this.internalNotes = res.data
          this.internalNotesSession = sid
          this.mergeNotes()
        }
      } catch (e) {
        // 轮询失败不打扰客服，下一轮再试
      }
    },

    /** 自己刚发的内部消息：不等下一轮轮询，马上显示 */
    addInternalNote(sessionId: string, note: any) {
      if (!note || this.sessionId !== sessionId) return
      if (this.internalNotesSession !== sessionId) {
        this.internalNotes = []
        this.internalNotesSession = sessionId
      }
      if (!this.internalNotes.some((n: any) => n.id === note.id)) {
        this.internalNotes = this.internalNotes.concat([note])
      }
      this.mergeNotes()
    },

    /**
     * 把内部消息按发送时间插进聊天记录里（this.data 里 internal=true 的那些）。
     * 每次整体重算：先去掉旧的内部消息，再插入当前这条会话的。
     * 历史是分页往上翻的，比已加载的最早一条还早的内部消息先不显示，翻到了再出来。
     */
    mergeNotes() {
      const toMs = (t: any): number => {
        if (!t) return NaN
        if (t instanceof Date) return t.getTime()
        const s = String(t)
        return /[TZ]/.test(s) ? Date.parse(s) : Date.parse(s.replace(/-/g, '/'))
      }
      const real = this.data.filter((m: any) => !m.internal)
      const notes: any[] = this.internalNotesSession === this.sessionId ? this.internalNotes : []
      let merged: any[] = real
      if (notes.length > 0) {
        const cutoff = this.isNeedUpdate && real.length > 0 ? toMs(real[0].sendTime) : -Infinity
        const myId = String(useUserStore().id || '')
        const items = notes
          .map((n: any) => ({
            id: 'note-' + n.id,
            recordId: '',
            internal: true,
            userType: 'INTERNAL',
            messageType: 'TEXT',
            content: n.body,
            sendTime: n.sendTime,
            senderUserId: n.senderId,
            senderName: n.senderName,
            mine: !!myId && String(n.senderId) === myId,
            ms: Number(n.sendTimeMs) || toMs(n.sendTime)
          }))
          .filter((n: any) => !(n.ms < cutoff))
          .sort((a: any, b: any) => a.ms - b.ms)
        merged = []
        let j = 0
        for (const r of real) {
          const rm = toMs(r.sendTime)
          while (j < items.length && items[j].ms <= rm) merged.push(items[j++])
          merged.push(r)
        }
        while (j < items.length) merged.push(items[j++])
      }
      const same =
        merged.length === this.data.length && merged.every((m: any, i: number) => m === this.data[i])
      if (!same) this.data = merged
    },

    setMobileInChat(val: any) {
      this.mobileInChat = val
    },
    isMsgTabAll() {
      return this.msgTab === MsgTabEnum.All
    },
    isMsgTabBlock() {
      return this.msgTab === MsgTabEnum.Black
    },
    setMsgTab(tab: MsgTabEnum = MsgTabEnum.All) {
      this.msgTab = tab
    },
    /**
     * 塞入值
     */
    setParams(param: any) {
      console.log('param111:', param)
      if (param?.sessionId) {
        this.sessionId = param.sessionId
      }
      if (param?.userId) {
        this.userId = param.userId
      }
      if (param?.senderImg) {
        this.senderImg = param.senderImg
      }
      if (param?.senderName) {
        this.senderName = param.senderName
      }
      if (param?.geterImg) {
        this.geterImg = param.geterImg
      }
      if (param?.fastReply) {
        this.fastReply = param.fastReply
      }
      if (param?.isNeedUpdate) {
        this.isNeedUpdate = true
      }
      if (param?.status) {
        this.status = param.status
      }
      if (param?.myself == false || param?.myself == true) {
        this.myself = param.myself
      }
      if (param.blackInfo) {
        this.setBlackInfo(param.blackInfo)
      }

      console.log('myseleffffff' + this.myself)
    },
    setBlackInfo(value: BlackInfo) {
      this.blackInfo = value
    },
    updateBlackInfo(value: BlackInfo) {
      const currentSessionChat = useChatListStore().getCurrentSessionChat
      if (currentSessionChat) {
        currentSessionChat.blackInfo = Object.assign({}, currentSessionChat.blackInfo, value)
        this.setBlackInfo(currentSessionChat.blackInfo)
      } else {
        this.setBlackInfo(Object.assign({}, this.blackInfo, value))
      }
    },
    unblock() {
      this.setBlock(false)
    },
    setBlock(value: boolean) {
      this.setBlackInfo({ block: value })
    },
    setTranslation(value: boolean) {
      this.translationSwitch = value
    },
    setAi(value: boolean) {
      this.aiSwitch = value
    },
    setTopPlace(value: boolean) {
      this.topPlace = value
    },
    //切换导航。 重新塞入
    switchMessage() {
      this.sessionId = ''
      this.userId = ''
      this.senderImg = ''
      this.senderName = ''
      this.geterImg = ''
      this.data = []
      this.newMessageNumber = 0
      this.blackInfo = { block: false }
      this.isNeedUpdate = true
      this.autoEndCountdownStartTime = ''
      this.inputMode = 'reply'
      this.internalNotes = []
      this.internalNotesSession = ''
    },
    setNewMessageNumber() {
      this.newMessageNumber = this.newMessageNumber + 1
    },
    setCountdownStartTime(value: string) {
      this.autoEndCountdownStartTime = value
    },
    /**
     * 塞入sessionId
     *
     * @param value
     */
    setSessionId(value: string) {
      this.sessionId = value
    },
    /**
     * 来了ws消息，塞入
     *
     * @param obj
     */
    setData(obj: any) {
      //console.log("obj--------------",obj)
      if (obj.messageType == MsgType.EVALUATE) {
        obj.contentJson = JSON.parse(obj.content || '')
        obj.senderName = this.senderName
      }
      this.data.push(obj)
    },
    clearData() {
      this.data = []
    },
    setUnReadId(value: string, obj: any) {
      console.log(value, obj)
      this.readSessions.set(value, obj)
    },
    clearFastReply() {
      this.fastReply = ''
    },

    //刷新聊天消息
    refreshMsgHistory() {
      if (this.sessionId) {
        const param: ISessionChatRecordRequest = {
          sessionId: this.sessionId,
          size: 20
        }
        const fetchHistory = this.colleagueMode
          ? () => this.fetchColleagueHistory(null)
          : () => queryCsHistoryRecord(param)
        fetchHistory().then((res: any) => {
          this.data = this.msgHistoryRecordConverter(res)
          this.mergeNotes()
        })
      }
    },

    /**
     * 同事对话取历史。
     *
     * 为什么不能直接 queryHistoryRecord({ sessionId, size })：监管接口在不传游标时是按 id 升序取前 size 条，
     * 也就是这条会话「最旧的」20 条。会话常常跨天（同一个玩家的会话一直沿用），客服看到的就是昨天的内容，
     * 轮询也永远拿不到新消息。要取最新的，得传「小于一个很大的 id」的游标，接口会按 id 降序返回；
     * 再反转成从旧到新，和普通聊天窗口的顺序一致。
     *
     * @param recordId 传了就取比它更早的（往上翻），不传取最新的
     */
    async fetchColleagueHistory(recordId: string | null) {
      const res: any = await queryHistoryRecord({
        sessionId: this.sessionId,
        size: 20,
        recordId: recordId || '9223372036854775807',
        recordSqlKeyword: 'LT'
      } as any)
      if (res && res.code == '0' && Array.isArray(res.data)) {
        return { ...res, data: res.data.slice().reverse() }
      }
      return res
    },

    // 同事对话里发完消息：稍等一下就补拉一次，让自己刚发的消息马上出现（不用等 3 秒一轮的轮询）
    pollSoon() {
      if (this.colleagueMode) {
        setTimeout(() => this.pollColleagueHistory(), 600)
      }
    },

    // 同事对话：拉最近 20 条，把还没有的新消息追加到末尾（不动已经加载的更早的历史，也不打断客服往上翻）
    pollColleagueHistory() {
      if (!this.sessionId || !this.colleagueMode) return
      const sid = this.sessionId
      this.fetchColleagueHistory(null).then((res: any) => {
        // 请求回来时已经切走了就丢掉
        if (this.sessionId !== sid || !this.colleagueMode) return
        const latest = this.msgHistoryRecordConverter(res)
        if (latest.length === 0) return
        const have = new Set(this.data.map((m: any) => m.id))
        const fresh = latest.filter((m: any) => !have.has(m.id))
        if (fresh.length > 0) {
          this.data = this.data.concat(fresh)
          this.mergeNotes()
        }
      })
    },

    //append聊天记录
    appendMsgHistory: function () {
      if (!this.sessionId) {
        return
      }
      if (!this.isNeedUpdate) {
        return
      }
      const size = 20
      const param: ISessionChatRecordRequest = {
        sessionId: this.sessionId,
        size: size,
        // 翻页游标：已加载的最早一条「正式消息」的 id（内部消息没有 recordId，不能当游标）
        recordId: this.data.find((m: any) => !m.internal)?.recordId || null
      }

      const fetchHistory = this.colleagueMode
        ? () => this.fetchColleagueHistory(param.recordId ?? null)
        : () => queryCsHistoryRecord(param)
      fetchHistory().then((res: any) => {
        const msgList = this.msgHistoryRecordConverter(res)
        if (msgList.length <= 0) {
          this.isNeedUpdate = false
          return
        }
        this.data = msgList.concat(this.data.filter((m: any) => !m.internal))
        if (msgList.length < size) {
          this.isNeedUpdate = false
        }
        this.mergeNotes()
      })
    },
    //查询消息历史
    msgHistoryRecordConverter(res: ISessionChatRecordResponseParam): any[] {
      if (!res || res.code != '0') {
        return []
      }
      if (!res.data || res.data.length <= 0) {
        return []
      }
      const arr: any[] = []
      res.data.forEach((item) => {
        const obj = {
          id: item?.id ?? uuidv4(),
          recordId: item?.id,
          userType: item?.senderInfo?.userType,
          messageType: item?.msgType || 'TEXT',
          content: item.body,
          sendTime: item?.sendTime ?? '',
          senderUserId: item?.senderInfo?.id,
          translationText: item?.translationText ?? '',
          withdraw: item?.withDraw ?? false,
          recordFileKeys: item?.recordFileKeys || [],
          contentJson: item?.msgType == MsgType.EVALUATE ? JSON.parse(item.body || '') : '',
          senderName: this.senderName,
          aiFlag: item?.senderInfo?.userType && item?.senderInfo?.userType == UserType.AI_SERVICE
        }
        arr.push(obj)
      })
      return arr
    },

    //删除消息《以后也可以用到消息撤回》
    delMessageListItem(id: string) {
      let index = -1
      if (this.data && this.data.length > 0) {
        for (let i = 0; i < this.data.length; i++) {
          if (this.data[i].id == id) {
            index = i
          }
        }
      }
      //删除当前消息
      if (index > -1) {
        this.data.splice(index, 1)[0]
      }
    },

    //消息撤回
    widthDrawMessage(id: string) {
      //接受 recordId
      let index = -1
      if (this.data && this.data.length > 0) {
        for (let i = 0; i < this.data.length; i++) {
          if (this.data[i].id == id) {
            index = i
          }
        }
      }

      if (index > -1) {
        const messageItem = this.data[index]
        //塞入一个
        const obj = {
          id: uuidv4(),
          recordId: '',
          userType: messageItem.userType,
          messageType: 'TEXT',
          content: 'This message was recalled',
          sendTime: timeUtilNowMoment(),
          senderUserId: useUserStore().id || '',
          translationText: '',
          withdraw: true
        }
        this.data.splice(index, 1, obj)
      }
    }
  }
})
